import React, { useState, useEffect, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import Breadcrums from "../../../common/breadcrumbs";
import { STATUS, SWAL_SETTINGS, THEME_COLORS, VERIFICATION_STATUS } from "../../../../../utils/Constants";
import {
  TrimText,
  formateDateWithMonth,
  handleServerValidations,
  hasP,
} from "../../../../../utils/commonfunction";
import * as teamManagers from "../../../services/teamManagers.service";
import * as clubAdmin from "../../../services/clubAdmin.service";
import { useTranslation } from "react-i18next";
import { CustomProvider, DateRangePicker, Placeholder, Tooltip, Whisper } from "rsuite";
import { useQuery } from "react-query";
import { AgGridReact } from "ag-grid-react";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";
import NoRowsOverlay from "../../../common/NoRowsOverlay";
import moment from "moment";

ModuleRegistry.registerModules([AllCommunityModule]);

const TeamManagerList = () => {
  const params = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const gridRef = useRef();

  const [searchData, setSearchData] = useState("");
  const [pageIndex] = useState(Number(params.pgno) - 1 || 0);
  const [perPage, setPerPage] = useState(10);
  const [clubs, setClubs] = useState([]);
  const [selectedClub, setSelectedClub] = useState("");
  const [activeTab, setActiveTab] = useState("verified");
  const [selectedRows, setSelectedRows] = useState([]);

  const breadcrumbs = [
    { title: t("sidebar_link_dashboard"), url: "/admin/dashboard" },
    { title: "Team Managers", url: "" },
  ];

  const onSelectionChanged = (event) => {
    if (event && event.api) {
      const selectedNodes = event.api.getSelectedNodes();
      const selectedData = selectedNodes.map((node) => node.data);
      setSelectedRows(selectedData);
    }
  };

  const Deletefunction = (row) => {
    Swal.fire({
      customClass: "swal-wide",
      title: t("msg_are_you_sure"),
      text: t("you_not_be_able_to_revert_this"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: THEME_COLORS.ADMIN_PRIMARY,
      cancelButtonColor: "#000",
      confirmButtonText: t("btn_yes_delete"),
      cancelButtonText: t("btn_cancel"),
    }).then((result) => {
      if (result.isConfirmed) {
        teamManagers
          .Delete(row.originalId)
          .then((response) => {
            if (response.success) {
              Swal.fire({
                icon: "success",
                text: response.message,
                ...SWAL_SETTINGS,
              });
              refetch();
            } else {
              Swal.fire({
                icon: "error",
                text: handleServerValidations(response),
                ...SWAL_SETTINGS,
              });
            }
          })
          .catch((error) => {
            Swal.fire({
              icon: "error",
              text: handleServerValidations(error),
              ...SWAL_SETTINGS,
            });
          });
      }
    });
  };

  const handleDeleteMultiple = () => {
    if (selectedRows.length === 0) return;
    Swal.fire({
      customClass: "swal-wide",
      title: t("msg_are_you_sure"),
      text: `Are you sure you want to delete ${selectedRows.length} selected record(s)? This action cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: THEME_COLORS.ADMIN_PRIMARY,
      cancelButtonColor: "#000",
      confirmButtonText: t("btn_yes_delete"),
      cancelButtonText: t("btn_cancel"),
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire({
          title: "Deleting...",
          text: "Please wait while selected records are being deleted",
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });

        const deletePromises = selectedRows.map((row) => teamManagers.Delete(row.originalId));
        Promise.all(deletePromises)
          .then(() => {
            setSelectedRows([]);
            if (gridRef.current && gridRef.current.api) {
              gridRef.current.api.deselectAll();
            }
            Swal.fire({
              icon: "success",
              title: "Deleted!",
              text: `${selectedRows.length} record(s) deleted successfully.`,
              ...SWAL_SETTINGS,
            });
            refetch();
          })
          .catch((error) => {
            Swal.fire({
              icon: "error",
              text: handleServerValidations(error),
              ...SWAL_SETTINGS,
            });
          });
      }
    });
  };

  useEffect(() => {
    clubAdmin.List({ limit: 1000, filters: { isProfileVerified: "verified", isBlocked: false } }).then(res => {
      if (res.success) {
        const activeClubs = (res.data.list || []).filter(
          c => !c.isDeleted && 
               c.isDeleted !== "true" && 
               !c.deletedAt && 
               c.status !== "DELETED" && 
               (c.isProfileVerified === "verified" || c.isProfileVerified === true || c.isProfileVerified === "true") && 
               !c.isBlocked
        );
        setClubs(activeClubs);
      }
    });
  }, []);

  // ================= FETCH ALL DATA =================
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["team-managers-list", selectedClub],
    queryFn: () => teamManagers.List({
      limit: 1000,
      lastKey: null,
      filters: { clubAdminId: selectedClub },
      sorting: {}
    }),
    refetchOnWindowFocus: false,
    retry: false,
    onError: (error) => {
      Swal.fire({
        icon: "error",
        text: handleServerValidations(error),
        ...SWAL_SETTINGS,
      });
    },
  });

  const ChangeStatus = (id, newStatus) => {
    Swal.fire({
      customClass: "swal-wide",
      title: t("msg_are_you_sure"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: THEME_COLORS.ADMIN_PRIMARY,
      cancelButtonColor: "#000",
      confirmButtonText: t("btn_yes"),
      cancelButtonText: t("btn_cancel")
    }).then((result) => {
      if (result.isConfirmed) {
        // Optimistic UI Update
        if (gridRef.current?.api) {
          const rowNode = gridRef.current.api.getRowNode(String(id));
          if (rowNode) {
            rowNode.setDataValue("status", newStatus);
          }
        }

        // API call
        teamManagers.Status({ status: newStatus }, id).then((response) => {
          if (response.success) {
            Swal.fire({
              icon: "success",
              text: response.message,
              ...SWAL_SETTINGS,
            });
          } else {
            // Revert
            if (gridRef.current?.api) {
              const rowNode = gridRef.current.api.getRowNode(String(id));
              if (rowNode) {
                rowNode.setDataValue(
                  "status",
                  newStatus === STATUS.ACTIVE ? STATUS.INACTIVE : STATUS.ACTIVE
                );
              }
            }
            Swal.fire({
              icon: "error",
              text: handleServerValidations(response),
              ...SWAL_SETTINGS,
            });
          }
        }).catch((error) => {
          // Revert
          if (gridRef.current?.api) {
            const rowNode = gridRef.current.api.getRowNode(String(id));
            if (rowNode) {
              rowNode.setDataValue(
                "status",
                newStatus === STATUS.ACTIVE ? STATUS.INACTIVE : STATUS.ACTIVE
              );
            }
          }
          Swal.fire({
            icon: "error",
            text: handleServerValidations(error),
            ...SWAL_SETTINGS,
          });
        });
      }
    });
  };

  const renderProfileStatus = (row) => {
    if (row?.isBlocked) {
      return <span className="badge badge-pill badge-danger-light fw-bold text-uppercase">Blocked</span>;
    }
    switch (row?.isProfileVerified) {
      case "verified":
        return <span className="badge badge-pill badge-success-light fw-bold text-uppercase">{t("label_verified")}</span>;
      case "rejected":
        return <span className="badge badge-pill badge-danger-light fw-bold text-uppercase">{t("label_rejected")}</span>;
      default:
        return <span className="badge badge-pill badge-warning-light fw-bold text-uppercase">{t("label_pending")}</span>;
    }
  };

  // ================= COLUMNS =================
  const colDefs = useMemo(() => [
    {
      headerName: "",
      field: "checkbox",
      maxWidth: 50,
      checkboxSelection: true,
      headerCheckboxSelection: true,
      sortable: false,
      filter: false,
      suppressMovable: true,
      lockPosition: true,
    },
    {
      headerName: t("list_heading_sno"),
      field: "srNo",
      maxWidth: 85,
      sortable: false,
      filter: false,
      suppressMovable: true,
      lockPosition: true,
      valueGetter: (params) => {
        if (!params.node || params.node.rowIndex === undefined) return "";
        return params.node.rowIndex + 1;
      },
      cellStyle: { borderRight: "1px solid #dee2e6", color: "#3c4858" },
    },
    {
      headerName: t("label_name"),
      field: "name",
      flex: 1,
      minWidth: 200,
      sortable: true,
      filter: true,
      cellClass: 'ag-cell-ellipsis',
    },
    {
      headerName: t("email"),
      field: "email",
      flex: 1,
      minWidth: 150,
      tooltipField: "email",
      cellRenderer: (params) => {
        return (
          <div
            style={{
              width: "100%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {params.value || "N/A"}
          </div>
        );
      },
    },
      {
      headerName: t("Club"),
      field: "clubName",
      flex: 1,
      minWidth: 150,
      tooltipField: "clubName",
      cellRenderer: (params) => {
        return (
          <div
            style={{
              width: "100%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {params.value || "N/A"}
          </div>
        );
      },
    },
    {
      headerName: t("label_verification"),
      field: "isProfileVerified",
      minWidth: 140,
      maxWidth: 140,
      sortable: true,
      filter: true,
      cellRenderer: (params) => renderProfileStatus(params.data),
      cellStyle: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }
    },
    {
      headerName: t("list_heading_status"),
      field: "status",
      filter: true,
      maxWidth: 110,
      cellStyle: {
        display: "flex",
        justifyContent: "center"
      },
      cellRenderer: (params) => {
        const currentStatus = params.value;
        const isActive = currentStatus === STATUS.ACTIVE;

        return (
          <label className={`custom-switch cp${!isActive ? " e_none" : ""}`}>
            <input
              type="checkbox"
              className="custom-switch-input cp"
              checked={isActive}
              onChange={() => {
                const newStatus = isActive ? STATUS.INACTIVE : STATUS.ACTIVE;
                ChangeStatus(params.data.originalId, newStatus);
              }}
            />
            <span className="custom-switch-indicator cp"></span>
          </label>
        );
      },
    },
    {
      headerName: t("list_heading_created_date"),
      field: "createdAt",
      filter: "agDateColumnFilter",
      minWidth: 140,
      maxWidth: 140,
      sortable: true,
      valueGetter: (params) =>
        params.data?.createdAt ? new Date(params.data.createdAt) : null,
      valueFormatter: (params) =>
        params.value ? formateDateWithMonth(params.value) : "N/A"
    },
    {
      headerName: t("list_heading_action"),
      minWidth: 100,
      maxWidth: 120,
      pinned: "right",
      sortable: false,
      filter: false,
      cellRenderer: (params) => {
        const row = params.data;
        return (
          <div className="btn-toolbar table-actions">
            <div className="btn-group btn-group-sm">
              <Whisper
                placement="top"
                trigger="hover"
                speaker={<Tooltip style={{ fontWeight: 'bold' }}>View</Tooltip>}
              >
                <button
                  type="button"
                  className="btn view"
                  onClick={() => navigate(`/admin/user-management/team-managers/view/${row.originalId}`)}
                >
                  <i className="ri-eye-fill"></i>
                </button>
              </Whisper>
              <Whisper
                placement="top"
                trigger="hover"
                speaker={<Tooltip style={{ fontWeight: 'bold' }}>Audit Logs</Tooltip>}
              >
                <button
                  type="button"
                  className="btn view ml-2"
                  style={{ backgroundColor: '#10b981', color: '#fff' }}
                  onClick={() => navigate(`/admin/user-management/audit-logs/${row.originalId}`)}
                >
                  <i className="ri-history-fill"></i>
                </button>
              </Whisper>
              <Whisper
                placement="top"
                trigger="hover"
                speaker={<Tooltip style={{ fontWeight: 'bold' }}>Delete</Tooltip>}
              >
                <button
                  type="button"
                  className="btn delete ml-2"
                  onClick={() => Deletefunction(row)}
                >
                  <i className="ri-delete-bin-6-fill"></i>
                </button>
              </Whisper>
            </div>
          </div>
        );
      }
    }
  ], [t, navigate]);


  const rowData = useMemo(() => {
    if (isLoading || !data?.data?.list) return null;

    let filteredList = data.data.list;

    return filteredList
      .map((row, i) => ({
        srNo: 0,
        originalId: row.id,
        name: `${row?.firstName ? TrimText(row.firstName, 15) : ""} ${row?.lastName ? TrimText(row.lastName, 15) : ""}`.trim() || "N/A",
        email: row?.email || "N/A",
        clubName: row?.clubEmail || "N/A",
        location:
          row?.city || row?.country
            ? `${row?.city ? TrimText(row.city, 20) : ""}${row?.city && row?.country ? ", " : ""}${row?.country ? TrimText(row.country, 20) : ""}`
            : "N/A",
        isProfileVerified: row?.isProfileVerified || "pending",
        isBlocked: row?.isBlocked || false,
        status: row.status,
        createdAt: row.createdAt,
      }))
      .filter((row) => {
        // Tab Filtering
        if (activeTab === "verified") {
          if (row.isProfileVerified !== "verified" || row.isBlocked) return false;
        } else if (activeTab === "pending") {
          if (row.isProfileVerified !== "pending" || row.isBlocked) return false;
        } else if (activeTab === "rejected_blocked") {
          if (row.isProfileVerified !== "rejected" && !row.isBlocked) return false;
        }

        // Search Filtering
        if (searchData) {
          const search = searchData.toLowerCase();
          return (
            row.name.toLowerCase().includes(search) ||
            row.email.toLowerCase().includes(search)
          );
        }
        return true;
      });
  }, [data, searchData, activeTab, isLoading]);

  // ================= AG GRID CONFIG =================
  const getAgGridConfig = useMemo(() => ({
    ref: gridRef,
    domLayout: "autoHeight",
    rowData: rowData,
    columnDefs: colDefs,
    rowSelection: "multiple",
    onSelectionChanged: onSelectionChanged,
    getRowId: (params) => String(params.data.originalId),
    noRowsOverlayComponent: NoRowsOverlay,
    pagination: true,
    paginationPageSize: perPage,
    paginationPageSizeSelector: [10, 20, 50, 100],
    suppressCellFocus: true,
    defaultColDef: {
      resizable: true,
      sortable: true,
      minWidth: 150,
      cellStyle: {
        padding: "10px 8px",
      },
      suppressHeaderMenuButton: true,
      suppressHeaderFilterButton: true,
    },
    headerHeight: 50,
    rowHeight: 45,
    onPaginationChanged: (params) => {
      if (params.api) {
        const currentPage = params.api.paginationGetCurrentPage();
        const pageSize = params.api.paginationGetPageSize();
        setPerPage(pageSize);
      }
    },
  }), [rowData, colDefs, perPage]);

  return (
    <>
      <Breadcrums data={breadcrumbs} />
      <div className="animation_fade">
        <div className="card custom-card overflow-hidden">
          <div className="card-body">
            <ul className="nav nav-tabs mb-4">
              <li className="nav-item">
                <button
                  className={`nav-link ${activeTab === "verified" ? "active fw-bold" : ""}`}
                  type="button"
                  onClick={() => setActiveTab("verified")}
                >
                  Team Managers
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link ${activeTab === "pending" ? "active fw-bold" : ""}`}
                  type="button"
                  onClick={() => setActiveTab("pending")}
                >
                  Pending Requests
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link ${activeTab === "rejected_blocked" ? "active fw-bold" : ""}`}
                  type="button"
                  onClick={() => setActiveTab("rejected_blocked")}
                >
                  Rejected / Blocked
                </button>
              </li>
            </ul>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div className="d-flex align-items-center gap-3">
                <h6 className="main-content-label mb-0">Team Managers</h6>
                {selectedRows.length > 0 && (
                  <button
                    type="button"
                    className="btn btn-danger btn-sm d-flex align-items-center gap-1"
                    onClick={handleDeleteMultiple}
                  >
                    <i className="ri-delete-bin-line"></i> Delete Selected ({selectedRows.length})
                  </button>
                )}
              </div>
              <div className="d-flex align-items-center">
                <div style={{ width: 300 }} className="me-3">
                  <input
                    type="text"
                    className="form-control"
                    placeholder={t("input_placeholder_search")}
                    value={searchData}
                    onChange={(e) => setSearchData(e.target.value)}
                    style={{ outline: "none", boxShadow: "none" }}
                  />
                </div>

                {/* date */}
                <div>
                  <CustomProvider>
                    <DateRangePicker
                      style={{ width: "150px" }}
                      placeholder={t("input_placeholder_date")}
                      placement="bottomEnd"
                      onChange={(value) => {
                        if (!gridRef || !gridRef?.current || !gridRef?.current?.api) return;

                        if (!value || value?.length !== 2) {
                          gridRef?.current?.api?.setFilterModel(null);
                          gridRef?.current?.api?.onFilterChanged();
                          return;
                        }
                        const [start, end] = value;
                        gridRef?.current?.api?.setFilterModel({
                          createdAt: {
                            type: "inRange",
                            dateFrom: moment(start).startOf("day").format("YYYY-MM-DD HH:mm:ss"),
                            dateTo: moment(end).endOf("day").format("YYYY-MM-DD HH:mm:ss")
                          }
                        });
                        gridRef?.current?.api?.onFilterChanged();
                      }}
                    />
                  </CustomProvider>
                </div>
              </div>
            </div>

            {isLoading ? (
              <Placeholder.Paragraph
                className="w-100 admin-placeholder"
                rowHeight={30}
                rowSpacing={5}
                active={true}
                rows={7}
              />
            ) : (
              <div className="ag-theme-alpine" style={{ width: '100%' }}>
                <AgGridReact {...getAgGridConfig} />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default TeamManagerList;
